import React, { useContext, useRef, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { GALLERY } from '../utils/constants'
import PropTypes from 'prop-types'
import { buildScopePath } from '../utils/url_parser'

export const GalleryContext = React.createContext({
  addResults: () => undefined,
  getImages: () => [],
  openGallery: () => undefined
})

const createInitialGalleryUrl = (slide, scope) => {
  const sitePath = buildScopePath(scope)
  const { slug: imageSlug, parent: { id, slug } = {} } = slide
  return `${sitePath}/${GALLERY}/${id}/${slug}/${imageSlug}`
}

const GalleryProvider = ({ scope, children }) => {
  const history = useHistory()
  const { location } = history

  const galleryResults = useRef([])

  const getImages = useCallback(() => {
    return galleryResults.current
  }, [galleryResults.current])

  const addResults = (newImages, page = 1, reset) => {
    if (reset) {
      galleryResults.current = []
    }

    const baseResultIndex = (page - 1) * 18
    const formattedResults = newImages.filter(item => item.images.length > 0)
      .map(({ parent_info: parent, images }, index) => {
        const total = images.length
        const resultIndex = baseResultIndex + index
        return images.map((img, imageIndex) => ({
          ...img,
          parent,
          total,
          resultIndex,
          relativeIndex: imageIndex
        }))
      }).flat()

    galleryResults.current = [...galleryResults.current, ...formattedResults]
  }

  const wasSaveButtonClicked = target => target?.matches('hd-save-dropdown') || !!target?.closest('hd-save-dropdown')

  const openGallery = (e, imageIndex = 0, resultIndex = 0) => {
    if (wasSaveButtonClicked(e.target)) {
      return true
    }
    const slide = galleryResults.current.find(img => img.resultIndex === resultIndex && img.relativeIndex === imageIndex)

    if (slide === undefined) {
      // eslint-disable-next-line no-console
      console.error(`Slide for result ${resultIndex} - image ${imageIndex} was not found`)
    }

    if (wasSaveButtonClicked(e.target)) {
      return true
    }
    e.stopPropagation()
    const path = {
      pathname: createInitialGalleryUrl(slide, scope),
      state: { background: location, backgroundProps: { resultIndex, imageIndex } }
    }
    history.push(path)
  }

  return (
    <GalleryContext.Provider value={{ addResults, openGallery, getImages }}>
      {children}
    </GalleryContext.Provider>
  )
}

function useGallery () {
  const context = useContext(GalleryContext)
  if (context === undefined) {
    throw new Error('useGallery has to be used within GalleryProvider')
  }
  return context
}

GalleryProvider.propTypes = {
  scope: PropTypes.string,
  children: PropTypes.node
}

export { GalleryProvider, useGallery }
